/**
 * @fileoverview In-memory data behind the User Management mocks.
 * @description The user directory as the user-invitations store lists it (seeded users plus
 * invitees), with Update, Deactivate and Reactivate User's changes layered on top and a version
 * per user for its ETag, held in module state for the browser session. Applies the backend's
 * rules: a stale If-Match is refused (412 / 3022), role and manager changes need an ACTIVE user
 * (409 / 3023), the manager must be eligible for the (new) role (403 / 3012), a deactivated user
 * can't be edited or deactivated again (409 / 3023, 3030), Deactivate moves the user's direct
 * reports to the user's own manager or refuses when that manager can't take them (409 / 3024),
 * and Reactivate needs a Sales role and an eligible manager (409 / 3031). Also derives each
 * user's allowed actions, the directory's KPI summary and filter options, the lifecycle reasons
 * and Edit User choices. Narrows what the signed-in caller may do the way the backend does: never
 * to their own row, and (unless they are the Super Admin, who manages every Sales user) only to
 * the users below them in the reporting line, with Edit needing `users:update` and Resend
 * `users:invite`. A command outside that is refused (403 / 2008). With nobody signed in the
 * caller is the Super Admin. Records each Deactivate and Reactivate it applies to the shared
 * lifecycle change feed, so the Sales roster mock follows. Never imported outside this feature's
 * mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useAuthStore } from '@/shared/lib/auth/auth.store'
import { recordUserLifecycleChange } from '@/shared/mocks/user-lifecycle-mock.store'

import {
  findMockState,
  listDirectoryUsers,
  listEligibleManagers,
  MOCK_ASSIGNABLE_ROLES,
  MOCK_STATES,
  pendingInvitationIdOf,
  restartInvitation,
  revokeInvitationOnDeactivation,
  toManagerRef,
} from './user-invitations-mock-store'

import type { MockDirectoryUser, MockPositionCode } from './user-invitations-mock-store'

/** The Update User body the mock accepts. */
export interface MockUpdateUserRequest {
  firstName?: string
  middleName?: string | null
  lastName?: string
  countryCode?: string
  mobileNumber?: string
  primaryRoleId?: string
  reportingManagerUserId?: string
  stateId?: string | null
}

/** The Deactivate User body the mock accepts. */
export interface MockDeactivateUserRequest {
  reasonCode?: string
  reasonNote?: string
}

/** The Reactivate User body the mock accepts. */
export interface MockReactivateUserRequest extends MockDeactivateUserRequest {
  roleId?: string
  reportingManagerUserId?: string
}

/** Why a request was refused: the HTTP status, app code and message. */
export type MockRefusal = [number, string, string]

/** How a reactivated user came back. */
export type MockReactivationMode = 'ACTIVE_RESTORE' | 'INVITATION_RESTART'

type MockAllowedAction =
  'EDIT' | 'DEACTIVATE' | 'REACTIVATE' | 'RESEND_INVITATION' | 'REVOKE_INVITATION'

/** Who is calling: their directory id (null for the Super Admin) and the privileges they hold. */
interface MockCaller {
  /** Null for the Super Admin, the root of the reporting line, who is not a directory member. */
  userId: string | null
  /** Null when nobody is signed in, which the mock treats as holding every privilege. */
  privileges: ReadonlySet<string> | null
}

/** The privilege an action needs beyond reaching the user; the others need nothing more. */
const ACTION_PRIVILEGES: Partial<Record<MockAllowedAction, string>> = {
  EDIT: 'users:update',
  RESEND_INVITATION: 'users:invite',
  REVOKE_INVITATION: 'users:invite',
}

/** The refusal for a command the caller may not take on this user. */
const FORBIDDEN: MockRefusal = [403, '2008', 'Forbidden']

/** The actions that act on the user's pending invitation, so need one to exist. */
const INVITATION_ACTIONS: ReadonlySet<MockAllowedAction> = new Set([
  'RESEND_INVITATION',
  'REVOKE_INVITATION',
])

const SALES_ROLES = MOCK_ASSIGNABLE_ROLES.filter((role) => role.roleCode.startsWith('SALES_'))

// The Super Admin is the root of the reporting line: counted in the total, never a member.
const SUPER_ADMIN_ROOT = 1
// The Super Admin is not a directory member, so a command they run is recorded under this id.
const SUPER_ADMIN_USER_ID = 'EMP-2000'

const NOTE_MAX_LENGTH = 500
const OTHER_REASON_CODE = 'OTHER'

const DEACTIVATION_REASONS = [
  { code: 'LEFT_ORGANISATION', label: 'Left the organisation' },
  { code: 'ROLE_NO_LONGER_REQUIRED', label: 'Role no longer required' },
  { code: 'DUPLICATE_ACCOUNT', label: 'Duplicate account' },
  { code: 'SECURITY_POLICY', label: 'Security policy' },
  { code: OTHER_REASON_CODE, label: 'Other' },
]
const REACTIVATION_REASONS = [
  { code: 'RETURNED_TO_ORGANISATION', label: 'Returned to the organisation' },
  { code: 'DEACTIVATED_IN_ERROR', label: 'Deactivated in error' },
  { code: OTHER_REASON_CODE, label: 'Other' },
]

/** The statuses in which each action applies, as the backend decides them. */
const ACTION_STATUSES: Record<MockAllowedAction, string[]> = {
  EDIT: ['INVITED', 'REGISTERED', 'ACTIVE'],
  DEACTIVATE: ['INVITED', 'REGISTERED', 'ACTIVE', 'INACTIVE', 'LOCKED', 'SUSPENDED'],
  REACTIVATE: ['DEACTIVATED'],
  RESEND_INVITATION: ['INVITED'],
  REVOKE_INVITATION: ['INVITED'],
}

const MOCK_BANK_NAMES = [
  'Saraswat Co-operative Bank',
  'Cosmos Co-operative Bank',
  'Abhyudaya Co-operative Bank',
]

const edits = new Map<string, Partial<MockDirectoryUser>>()
const versions = new Map<string, number>()

/**
 * @description Lists the directory with every change applied.
 * @returns {MockDirectoryUser[]} The directory, unfiltered.
 */
export function listManagedUsers(): MockDirectoryUser[] {
  return listDirectoryUsers().map((user) => ({ ...user, ...edits.get(user.userId) }))
}

/**
 * @description Finds one directory user, with their changes applied.
 * @param {string} userId - The user's id.
 * @returns {MockDirectoryUser | undefined} The user.
 */
export function findManagedUser(userId: string): MockDirectoryUser | undefined {
  return listManagedUsers().find((user) => user.userId === userId)
}

/**
 * @description A user's current ETag.
 * @param {string} userId - The user's id.
 * @returns {string} The quoted, strong `um2.` version.
 */
export function etagOf(userId: string): string {
  return `"um2.${versions.get(userId) ?? 1}"`
}

/**
 * @description Moves a user to their next version, so every ETag handed out before is stale.
 * @param {string} userId - The user's id.
 */
function bumpVersion(userId: string): void {
  versions.set(userId, (versions.get(userId) ?? 1) + 1)
}

/**
 * @description Stores changes to a user.
 * @param {string} userId - The user's id.
 * @param {Partial<MockDirectoryUser>} changes - The fields that changed.
 */
function applyChanges(userId: string, changes: Partial<MockDirectoryUser>): void {
  edits.set(userId, { ...edits.get(userId), ...changes })
  bumpVersion(userId)
}

/**
 * @description Who is calling, from the signed-in session: a directory member when their email is
 * in the directory, otherwise the Super Admin (the root, who is not a member). Nobody signed in is
 * the Super Admin too.
 * @returns {MockCaller} The caller.
 */
function currentCaller(): MockCaller {
  const session = useAuthStore.getState().user
  const email = session?.email.trim().toLowerCase()
  const member = email
    ? listManagedUsers().find((user) => user.email.toLowerCase() === email)
    : undefined
  return {
    userId: member?.userId ?? null,
    privileges: session ? new Set(session.privileges) : null,
  }
}

/**
 * @description Whether a user is somewhere below another in the reporting line.
 * @param {MockDirectoryUser} user - The user.
 * @param {string} managerId - The would-be manager's id.
 * @returns {boolean} True when the reporting line from the user reaches the manager.
 */
function reportsUnder(user: MockDirectoryUser, managerId: string): boolean {
  const byId = new Map(listManagedUsers().map((candidate) => [candidate.userId, candidate]))
  const visited = new Set<string>()
  let next = user.reportingManager?.userId
  while (next !== undefined && !visited.has(next)) {
    if (next === managerId) {
      return true
    }
    visited.add(next)
    next = byId.get(next)?.reportingManager?.userId
  }
  return false
}

/**
 * @description Whether the caller may act on a user at all: the Super Admin may on every Sales
 * user; anyone else only on users below them in the reporting line, never on themselves.
 * @param {MockDirectoryUser} user - The user.
 * @param {MockCaller} caller - Who is calling.
 * @returns {boolean} True when the user is within the caller's reach.
 */
function canReach(user: MockDirectoryUser, caller: MockCaller): boolean {
  return (
    caller.userId === null || (user.userId !== caller.userId && reportsUnder(user, caller.userId))
  )
}

/**
 * @description Whether the caller holds the privilege an action needs, if it needs one.
 * @param {MockAllowedAction} action - The action.
 * @param {MockCaller} caller - Who is calling.
 * @returns {boolean} True when the caller may take the action, reach aside.
 */
function holdsPrivilegeFor(action: MockAllowedAction, caller: MockCaller): boolean {
  const privilege = ACTION_PRIVILEGES[action]
  return privilege === undefined || caller.privileges === null || caller.privileges.has(privilege)
}

/**
 * @description What the caller may do to a user: the actions that apply in the user's status, and
 * only for a user within the caller's reach, never themselves, and with the privilege the action
 * needs (the backend's advisory `allowedActions`).
 * @param {MockDirectoryUser} user - The user.
 * @returns {MockAllowedAction[]} The actions, in the backend's order.
 */
export function allowedActionsOf(user: MockDirectoryUser): MockAllowedAction[] {
  const caller = currentCaller()
  if (!canReach(user, caller)) {
    return []
  }
  const order: MockAllowedAction[] = [
    'EDIT',
    'DEACTIVATE',
    'REACTIVATE',
    'RESEND_INVITATION',
    'REVOKE_INVITATION',
  ]
  return order.filter(
    (action) =>
      ACTION_STATUSES[action].includes(user.status) &&
      holdsPrivilegeFor(action, caller) &&
      (!INVITATION_ACTIONS.has(action) || pendingInvitationIdOf(user.userId) !== null),
  )
}

/**
 * @description Refuses a command the caller may not take on this user, whatever the user's status:
 * a user outside their reach (or themselves), or an action they hold no privilege for.
 * @param {MockDirectoryUser} user - The user the command targets.
 * @param {MockAllowedAction} action - The command's action.
 * @returns {MockRefusal | null} The refusal, or null when the caller may take it.
 */
function checkAuthority(user: MockDirectoryUser, action: MockAllowedAction): MockRefusal | null {
  const caller = currentCaller()
  return canReach(user, caller) && holdsPrivilegeFor(action, caller) ? null : FORBIDDEN
}

/**
 * @description Shapes a directory user as GET /users returns them: with the actions the caller
 * may take, and the invitation to resend while one is pending.
 * @param {MockDirectoryUser} user - The directory user.
 * @returns {object} The directory entry.
 */
export function toDirectoryEntry(user: MockDirectoryUser) {
  const allowedActions = allowedActionsOf(user)
  return {
    ...user,
    allowedActions,
    pendingInvitationId: allowedActions.includes('RESEND_INVITATION')
      ? pendingInvitationIdOf(user.userId)
      : null,
  }
}

/**
 * @description A made-up but stable list of up to 3 banks for a user; the last one of a full list
 * is only proposed to them. A deactivated user has none.
 * @param {MockDirectoryUser} user - The user.
 * @returns {object[]} The assigned banks.
 */
function mockAssignedBankList(user: MockDirectoryUser) {
  if (user.status === 'DEACTIVATED') {
    return []
  }
  const count = [...user.userId].reduce((sum, char) => sum + char.charCodeAt(0), 0) % 4
  return MOCK_BANK_NAMES.slice(0, count).map((bankName, index) => ({
    prospectId: `mock-prospect-${user.userId}-${index + 1}`,
    bankName,
    relation: index === 2 ? 'PROPOSED_PROSPECT_OWNER' : 'PROSPECT_OWNER',
  }))
}

/**
 * @description Shapes a directory user as GET /users/{userId} returns them.
 * @param {MockDirectoryUser} user - The directory user.
 * @returns {object} The user's details.
 */
export function toUserDetail(user: MockDirectoryUser) {
  const { reportingManager } = user
  const reportingLineStatus =
    user.status === 'DEACTIVATED' ? 'ENDED' : user.status === 'INVITED' ? 'PENDING' : 'ACTIVE'
  const { allowedActions, pendingInvitationId } = toDirectoryEntry(user)
  return {
    userId: user.userId,
    employeeCode: user.employeeCode,
    firstName: user.firstName,
    middleName: user.middleName,
    lastName: user.lastName,
    displayName: user.displayName,
    email: user.email,
    countryCode: user.countryCode,
    mobileNumber: user.mobileNumber,
    avatar: null,
    primaryRole: user.roles[0] ?? null,
    department: user.department,
    state: user.state,
    reportingManager: reportingManager && {
      userId: reportingManager.userId,
      displayName: reportingManager.displayName,
      positionCode: reportingManager.positionCode,
      reportingLineStatus,
    },
    joinedAt: user.joinedAt,
    dateOfJoining: user.dateOfJoining,
    status: user.status,
    directoryStatus: user.directoryStatus,
    allowedActions,
    pendingInvitationId,
    assignedBanks: mockAssignedBankList(user),
  }
}

/**
 * @description Counts the directory by Sales position, for the KPI cards, with each position's
 * Active, Pending and Inactive counts. The total also counts the Super Admin root, who is never a
 * directory member (as `UsersSummary.totalUsers` documents).
 * @returns {object} The summary.
 */
export function summarize() {
  const users = listManagedUsers()
  const countOf = (matching: MockDirectoryUser[]) => ({
    active: matching.filter((user) => user.directoryStatus === 'ACTIVE').length,
    pending: matching.filter((user) => user.directoryStatus === 'PENDING').length,
    inactive: matching.filter((user) => user.directoryStatus === 'INACTIVE').length,
  })
  const byPosition = (code: MockPositionCode) =>
    users.filter((user) => user.primaryPosition.code === code)
  const salesHeads = byPosition('SALES_HEAD')
  const salesManagers = byPosition('SALES_MANAGER')
  const salesExecutives = byPosition('SALES_EXECUTIVE')
  return {
    totalUsers:
      salesHeads.length + salesManagers.length + salesExecutives.length + SUPER_ADMIN_ROOT,
    salesHeads: salesHeads.length,
    salesManagers: salesManagers.length,
    salesExecutives: salesExecutives.length,
    statusCounts: {
      all: countOf([...salesHeads, ...salesManagers, ...salesExecutives]),
      salesHeads: countOf(salesHeads),
      salesManagers: countOf(salesManagers),
      salesExecutives: countOf(salesExecutives),
    },
    evaluatedAt: new Date().toISOString(),
  }
}

/**
 * @description The Role filter's choices: the positions present in the directory, by name.
 * @returns {object[]} The choices.
 */
export function roleFilterOptions() {
  const byId = new Map(
    listManagedUsers()
      .flatMap((user) => user.roles.slice(0, 1))
      .map((role) => [role.roleId, { kind: 'ROLE', ...role }] as const),
  )
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * @description The Reporting Manager filter's choices: the managers directory members report to.
 * @returns {object[]} The choices.
 */
export function managerFilterOptions() {
  const byId = new Map(
    listManagedUsers()
      .flatMap((user) => (user.reportingManager ? [user.reportingManager] : []))
      .map((manager) => [manager.userId, { kind: 'REPORTING_MANAGER', ...manager }] as const),
  )
  return [...byId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName))
}

/**
 * @description The State filter's choices: All India, then each catalogue state.
 * @returns {object[]} The choices.
 */
export function stateFilterOptions() {
  return [
    { kind: 'STATE', value: 'ALL_INDIA', label: 'All India', synthetic: true },
    ...MOCK_STATES.map((state) => ({ kind: 'STATE', value: state.stateId, label: state.name })),
  ]
}

/**
 * @description GET /users/configuration: the reasons a deactivation or reactivation may give, and
 * the note's length limit.
 * @returns {object} The configuration.
 */
export function userManagementConfiguration() {
  return {
    lifecycleReasons: { deactivation: DEACTIVATION_REASONS, reactivation: REACTIVATION_REASONS },
    limits: { reasonNoteMaxLength: NOTE_MAX_LENGTH },
  }
}

/**
 * @description Edit User's role choices: the primary Sales roles.
 * @returns {object[]} The choices.
 */
export function roleEditOptions() {
  return SALES_ROLES.map(({ roleId, roleCode, name }) => ({ kind: 'ROLE', roleId, roleCode, name }))
}

/**
 * @description Edit User's manager choices for a role, never the user themselves.
 * @param {string} userId - The user being edited.
 * @param {string} roleId - The proposed role.
 * @returns {object[]} The choices.
 */
export function managerEditOptions(userId: string, roleId: string) {
  return listEligibleManagers(roleId)
    .filter((manager) => manager.userId !== userId)
    .map((manager) => ({ kind: 'REPORTING_MANAGER', ...manager }))
}

/**
 * @description Checks a command's If-Match against the user's current version.
 * @param {string} userId - The user's id.
 * @param {string} ifMatch - The If-Match header ('' when absent).
 * @returns {MockRefusal | null} Why it was refused, or null when it matches.
 */
function checkPrecondition(userId: string, ifMatch: string): MockRefusal | null {
  if (!ifMatch) {
    return [428, '3017', 'Required User Management request precondition is missing']
  }
  if (ifMatch !== etagOf(userId)) {
    return [412, '3022', 'User management state changed; fetch current representation and retry']
  }
  return null
}

/**
 * @description Checks a lifecycle command's reason: a known code for the action, with a note
 * (within the limit) when the reason is Other.
 * @param {{ code: string; label: string }[]} reasons - The reasons the action accepts.
 * @param {MockDeactivateUserRequest} request - The command's body.
 * @returns {MockRefusal | null} Why it was refused, or null when the reason is valid.
 */
function checkReason(
  reasons: { code: string; label: string }[],
  request: MockDeactivateUserRequest,
): MockRefusal | null {
  const note = request.reasonNote?.trim() ?? ''
  if (!reasons.some((reason) => reason.code === request.reasonCode)) {
    return [400, '0001', 'Validation failed']
  }
  if ((request.reasonCode === OTHER_REASON_CODE && note === '') || note.length > NOTE_MAX_LENGTH) {
    return [400, '0001', 'Validation failed']
  }
  return null
}

/**
 * @description Applies an update, checked like the backend checks it.
 * @param {MockDirectoryUser} user - The user, as currently stored.
 * @param {MockUpdateUserRequest} request - The changed fields.
 * @param {string} ifMatch - The If-Match header.
 * @returns {MockRefusal | null} Why it was refused, or null once applied.
 */
export function applyUpdate(
  user: MockDirectoryUser,
  request: MockUpdateUserRequest,
  ifMatch: string,
): MockRefusal | null {
  const precondition = checkAuthority(user, 'EDIT') ?? checkPrecondition(user.userId, ifMatch)
  if (precondition) {
    return precondition
  }
  if (!ACTION_STATUSES.EDIT.includes(user.status)) {
    return [409, '3023', 'User update is not permitted in the current state']
  }

  const changesPlacement = request.primaryRoleId ?? request.reportingManagerUserId
  if (changesPlacement && user.status !== 'ACTIVE') {
    return [409, '3023', 'User update is not permitted in the current state']
  }

  const state = findMockState(request.stateId)
  if (request.stateId && !state) {
    return [400, '0001', 'Validation failed']
  }

  const role = request.primaryRoleId
    ? SALES_ROLES.find((candidate) => candidate.roleId === request.primaryRoleId)
    : undefined
  const roleId = role?.roleId ?? user.roles[0]?.roleId ?? ''
  const managerId = request.reportingManagerUserId ?? user.reportingManager?.userId
  if (
    changesPlacement &&
    !managerEditOptions(user.userId, roleId).some((manager) => manager.userId === managerId)
  ) {
    return [403, '3012', 'Reporting manager is not eligible']
  }

  const firstName = request.firstName ?? user.firstName
  const lastName = request.lastName ?? user.lastName
  applyChanges(user.userId, {
    firstName,
    lastName,
    displayName: `${firstName} ${lastName}`,
    ...(request.middleName !== undefined && { middleName: request.middleName || null }),
    ...(request.mobileNumber && {
      countryCode: request.countryCode ?? null,
      mobileNumber: request.mobileNumber,
    }),
    // A null state is All India.
    ...(request.stateId !== undefined && { state }),
    ...(role && {
      roles: [{ roleId: role.roleId, roleCode: role.roleCode, name: role.name }],
      primaryPosition: { code: role.roleCode as MockPositionCode, name: role.name },
    }),
    ...(changesPlacement && { reportingManager: toManagerRef(managerId) }),
  })
  return null
}

/**
 * @description Deactivates a user, checked like the backend checks it. Their direct reports move
 * to the user's own manager, or the request is refused when that manager can't take them all.
 * @param {MockDirectoryUser} user - The user, as currently stored.
 * @param {MockDeactivateUserRequest} request - The reason.
 * @param {string} ifMatch - The If-Match header.
 * @returns {MockRefusal | null} Why it was refused, or null once applied.
 */
export function applyDeactivation(
  user: MockDirectoryUser,
  request: MockDeactivateUserRequest,
  ifMatch: string,
): MockRefusal | null {
  const refusal =
    checkAuthority(user, 'DEACTIVATE') ??
    checkPrecondition(user.userId, ifMatch) ??
    checkReason(DEACTIVATION_REASONS, request)
  if (refusal) {
    return refusal
  }
  if (!ACTION_STATUSES.DEACTIVATE.includes(user.status)) {
    return [409, '3030', 'User lifecycle state does not allow this action']
  }

  const reports = listManagedUsers().filter(
    (candidate) =>
      candidate.status !== 'DEACTIVATED' && candidate.reportingManager?.userId === user.userId,
  )
  const recipient = user.reportingManager
  const recipientCanTakeReports = reports.every(
    (report) =>
      recipient !== null &&
      listEligibleManagers(report.roles[0]?.roleId ?? '').some(
        (manager) => manager.userId === recipient.userId,
      ),
  )
  if (!recipientCanTakeReports) {
    return [409, '3024', 'No eligible recipient for the direct reports']
  }

  for (const report of reports) {
    applyChanges(report.userId, { reportingManager: toManagerRef(recipient?.userId) })
  }
  if (user.status === 'INVITED') {
    revokeInvitationOnDeactivation(user.userId)
  }
  applyChanges(user.userId, { status: 'DEACTIVATED', directoryStatus: 'INACTIVE' })
  recordUserLifecycleChange({
    kind: 'DEACTIVATED',
    userId: user.userId,
    recipientUserId: recipient?.userId ?? null,
    movedReportUserIds: reports.map((report) => report.userId),
    actingUserId: currentCaller().userId ?? SUPER_ADMIN_USER_ID,
  })
  return null
}

/**
 * @description Reactivates a deactivated user, checked like the backend checks it. A user who had
 * activated comes back ACTIVE; one who never did is invited again.
 * @param {MockDirectoryUser} user - The user, as currently stored.
 * @param {MockReactivateUserRequest} request - The role, manager and reason.
 * @param {string} ifMatch - The If-Match header.
 * @returns {MockRefusal | MockReactivationMode} Why it was refused, or how the user came back.
 */
export function applyReactivation(
  user: MockDirectoryUser,
  request: MockReactivateUserRequest,
  ifMatch: string,
): MockRefusal | MockReactivationMode {
  const refusal =
    checkAuthority(user, 'REACTIVATE') ??
    checkPrecondition(user.userId, ifMatch) ??
    checkReason(REACTIVATION_REASONS, request)
  if (refusal) {
    return refusal
  }
  if (!ACTION_STATUSES.REACTIVATE.includes(user.status)) {
    return [409, '3030', 'User lifecycle state does not allow this action']
  }

  const role = SALES_ROLES.find((candidate) => candidate.roleId === request.roleId)
  const manager = listEligibleManagers(role?.roleId ?? '').find(
    (candidate) => candidate.userId === request.reportingManagerUserId,
  )
  if (!role || !manager || manager.userId === user.userId) {
    return [409, '3031', 'Reactivation role or reporting manager is not valid']
  }

  const neverActivated = user.joinedAt === null
  if (neverActivated) {
    restartInvitation(user.userId)
  }
  applyChanges(user.userId, {
    roles: [{ roleId: role.roleId, roleCode: role.roleCode, name: role.name }],
    primaryPosition: { code: role.roleCode as MockPositionCode, name: role.name },
    reportingManager: toManagerRef(manager.userId),
    status: neverActivated ? 'INVITED' : 'ACTIVE',
    directoryStatus: neverActivated ? 'PENDING' : 'ACTIVE',
  })
  recordUserLifecycleChange({
    kind: 'REACTIVATED',
    userId: user.userId,
    roleName: role.name,
    reportingManagerUserId: manager.userId,
    invitedAgain: neverActivated,
  })
  return neverActivated ? 'INVITATION_RESTART' : 'ACTIVE_RESTORE'
}
